import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  allRecordsCql,
  CourseReservesDto,
  CourseReservesItemDto,
} from '@app/components';
import { usePaging, useQueryParams } from '@app/hooks';
import { escapeForbiddenSymbolsInQuery, makeRequest } from '@app/utils';
import { defaultRecordsPerPage, Resource, SortDirection } from '@app/constants';

export const useCourseReservesFetch = (
  pageNumber: number,
  searchQuery: string
): Resource<CourseReservesItemDto, CourseReservesDto> => {
  const [pages, setPages] = useState<CourseReservesItemDto[][]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const escapedQuery = escapeForbiddenSymbolsInQuery(searchQuery);
  const { sort = SortDirection.asc } = useQueryParams();

  const sortQuery = `sortBy name/sort.${sort}`;

  const courseReservesQuery = useQuery(
    ['courseReservesPage', pageNumber, searchQuery, sort],
    () =>
      makeRequest<CourseReservesDto>({
        url: '/opac-courses/courses',
        params: {
          status: 'active',
          limit: defaultRecordsPerPage,
          ...(pageNumber > 1 && {
            offset: defaultRecordsPerPage * (pageNumber - 1),
          }),
          ...{
            query: searchQuery
              ? `name="${escapedQuery}*" OR courseListing.instructorObjects="${escapedQuery}*" OR courseNumber="${escapedQuery}*" OR department.name="${escapedQuery}*" ${sortQuery}`
              : `${allRecordsCql} ${sortQuery}`,
          },
        },
      })
  );

  useEffect(() => {
    if (courseReservesQuery.data?.totalRecords !== undefined) {
      setTotalRecords(courseReservesQuery.data.totalRecords);
    }
  }, [courseReservesQuery.data?.totalRecords]);

  usePaging<CourseReservesItemDto>({
    areRecordsFetched: courseReservesQuery.isSuccess,
    records: courseReservesQuery.data?.courses,
    isNewPageRequest: pageNumber !== 1,
    pageNumber,
    setPages,
  });

  return {
    pages,
    recordKey: 'id',
    totalRecords,
    queryResult: courseReservesQuery,
  };
};
